import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtUSD } from "../lib/format";
import { Plus, Pencil, Trash2, Search, Settings2, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import WalletActions from "../components/WalletActions";

const isStale = (iso) =>
  !iso || Date.now() - new Date(iso).getTime() > 7 * 24 * 60 * 60 * 1000;

const usdtToUsd = (usdt, chargePct) => {
  const c = Number(chargePct) || 0;
  const div = 1 - c / 100;
  if (div <= 0) return 0;
  return (Number(usdt) || 0) / div;
};

/**
 * Generic wallet page with filters, custom statuses, action menu and history.
 * Configurable by props for paypal (card + linked cashapps) and venmo (simpler).
 */
export default function WalletPage({
  type,
  title,
  subtitle,
  prefix,
  enableCard = false,
  enableCashappLinks = false,
  processorMode = false,
}) {
  const EMPTY = {
    name: "",
    status: "active",
    balance: 0,
    notes: "",
    card_number: "",
    linked_cashapp_ids: [],
    charge_percent: 0,
  };

  const [list, setList] = useState([]);
  const [cashapps, setCashapps] = useState([]);
  const [opts, setOpts] = useState({ statuses: ["active", "inactive", "frozen"] });
  const [loading, setLoading] = useState(true);
  const loadedOnceRef = useRef(false);
  const [procTotals, setProcTotals] = useState({
    total_balance_update_usdt: 0,
    total_balance_update_usd: 0,
    total_withdrawal_usdt: 0,
    total_withdrawal_usd: 0,
  });

  const [filters, setFilters] = useState({
    q: "",
    status: "all",
    sort: "default",
    minBalance: "",
    staleOnly: false,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [optsOpen, setOptsOpen] = useState(false);
  const [optsDraft, setOptsDraft] = useState([]);
  const [statusDraft, setStatusDraft] = useState("");
  const [updateBalanceFor, setUpdateBalanceFor] = useState(null);
  const [updateBalanceValue, setUpdateBalanceValue] = useState("");
  const [updateBalanceNotes, setUpdateBalanceNotes] = useState("");

  const load = async (options = {}) => {
    const showLoading = options.showLoading ?? !loadedOnceRef.current;
    if (showLoading) setLoading(true);
    try {
      const params = { type };
      if (filters.q) params.q = filters.q;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.minBalance !== "") params.min_balance = Number(filters.minBalance);
      if (filters.staleOnly) params.not_updated_days = 7;
      const reqs = [
        api.get("/wallets", { params }),
        api.get(`/wallet-options/${type}`).catch(() => ({ data: { statuses: ["active"] } })),
      ];
      if (enableCashappLinks) {
        reqs.push(api.get("/wallets", { params: { type: "cashapp" } }).catch(() => ({ data: [] })));
      }
      const [r, o, ca] = await Promise.all(reqs);
      setList(r.data);
      setOpts(o.data);
      if (ca) setCashapps(ca.data);
      if (processorMode) {
        try {
          const t = await api.get(`/processor-totals/${type}`);
          setProcTotals(t.data);
        } catch {
          // keep zeros
        }
      }
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [filters, type]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, status: opts.statuses?.[0] || "active" });
    setOpen(true);
  };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      ...EMPTY,
      ...w,
      linked_cashapp_ids: w.linked_cashapp_ids || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, type, balance: Number(form.balance) || 0 };
      if (!enableCard) delete payload.card_number;
      if (!enableCashappLinks) delete payload.linked_cashapp_ids;
      if (processorMode) {
        payload.charge_percent = Number(form.charge_percent) || 0;
      } else {
        delete payload.charge_percent;
      }
      delete payload.rate_usd_to_usdt;
      if (editing) {
        await api.put(`/wallets/${editing.id}`, payload);
        toast.success(`${title.replace(/s$/, "")} updated`);
      } else {
        await api.post("/wallets", payload);
        toast.success(`${title.replace(/s$/, "")} added`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.name}"?`)) return;
    try {
      await api.delete(`/wallets/${w.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openUpdateBalance = (wallet) => {
    setUpdateBalanceFor(wallet);
    setUpdateBalanceValue(String(wallet.balance ?? 0));
    setUpdateBalanceNotes("");
  };

  const submitUpdateBalance = async () => {
    try {
      const nb = Number(updateBalanceValue);
      if (Number.isNaN(nb)) return toast.error("Enter a valid balance");
      await api.post("/transactions", {
        wallet_id: updateBalanceFor.id,
        kind: "balance_update",
        new_balance: nb,
        notes: updateBalanceNotes,
      });
      toast.success("Balance updated");
      setUpdateBalanceFor(null);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openOpts = () => {
    setOptsDraft([...(opts.statuses || [])]);
    setStatusDraft("");
    setOptsOpen(true);
  };
  const addStatus = () => {
    const v = statusDraft.trim();
    if (!v) return;
    if (optsDraft.some((x) => x.toLowerCase() === v.toLowerCase()))
      return toast.message(`"${v}" already exists`);
    setOptsDraft([...optsDraft, v]);
    setStatusDraft("");
  };
  const saveOpts = async () => {
    try {
      await api.put(`/wallet-options/${type}`, { statuses: optsDraft });
      toast.success("Statuses updated");
      setOptsOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const sortedList = useMemo(() => {
    if (filters.sort === "name-asc")
      return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (filters.sort === "name-desc")
      return [...list].sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    if (filters.sort === "balance-desc")
      return [...list].sort((a, b) => (b.balance || 0) - (a.balance || 0));
    if (filters.sort === "balance-asc")
      return [...list].sort((a, b) => (a.balance || 0) - (b.balance || 0));
    return list;
  }, [list, filters.sort]);

  const totalBalance = useMemo(
    () => list.reduce((s, w) => s + (w.balance || 0), 0),
    [list]
  );
  const totalBalanceUSD = useMemo(
    () =>
      list.reduce(
        (s, w) => s + usdtToUsd(w.balance, w.charge_percent),
        0
      ),
    [list]
  );
  const staleCount = useMemo(
    () => list.filter((w) => isStale(w.updated_at)).length,
    [list]
  );

  const activeFilters =
    (filters.q ? 1 : 0) +
    (filters.status !== "all" ? 1 : 0) +
    (filters.minBalance !== "" ? 1 : 0) +
    (filters.sort !== "default" ? 1 : 0) +
    (filters.staleOnly ? 1 : 0);

  const colCount =
    4 + (enableCard ? 1 : 0) + (enableCashappLinks ? 1 : 0) + (processorMode ? 2 : 0);

  return (
    <Layout
      title={title}
      subtitle={subtitle}
      testId={`${prefix}-page`}
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={openOpts}
            className="h-9"
            data-testid={`${prefix}-manage-options-btn`}
          >
            <Settings2 size={14} className="mr-1.5" /> Manage statuses
          </Button>
          <Button
            onClick={openCreate}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid={`add-${prefix}-btn`}
          >
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>
      }
    >
      {/* Summary */}
      {processorMode ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              {title} (filtered)
            </div>
            <div className="font-mono text-[22px] font-medium mt-1">{list.length}</div>
          </div>
          <div className="stat-card p-4" data-testid={`${prefix}-total-bal-update-usdt`}>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Total balance update (USDT)
            </div>
            <div className="font-mono text-[22px] font-medium mt-1">
              {Number(procTotals.total_balance_update_usdt || 0).toLocaleString("en-US", {
                maximumFractionDigits: 4,
              })}
            </div>
          </div>
          <div className="stat-card p-4" data-testid={`${prefix}-total-bal-update-usd`}>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Total balance update (USD)
            </div>
            <div className="font-mono text-[22px] font-medium mt-1 text-emerald-700">
              {fmtUSD(procTotals.total_balance_update_usd || 0)}
            </div>
          </div>
          <div className="stat-card p-4" data-testid={`${prefix}-total-withdrawal-usdt`}>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Total withdrawal (USDT)
            </div>
            <div className="font-mono text-[22px] font-medium mt-1">
              {Number(procTotals.total_withdrawal_usdt || 0).toLocaleString("en-US", {
                maximumFractionDigits: 4,
              })}
            </div>
          </div>
          <div className="stat-card p-4" data-testid={`${prefix}-total-withdrawal-usd`}>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Total withdrawal (USD)
            </div>
            <div className="font-mono text-[22px] font-medium mt-1 text-rose-700">
              {fmtUSD(procTotals.total_withdrawal_usd || 0)}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              {title} (filtered)
            </div>
            <div className="font-mono text-[22px] font-medium mt-1">{list.length}</div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Total balance
            </div>
            <div className="font-mono text-[22px] font-medium mt-1">
              {fmtUSD(totalBalance)}
            </div>
          </div>
          <div className="stat-card p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Stale (7d+)
            </div>
            <div
              className={`font-mono text-[22px] font-medium mt-1 ${
                staleCount > 0 ? "text-amber-700" : "text-zinc-700"
              }`}
            >
              {staleCount}
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="stat-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={13}
            strokeWidth={1.7}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search name, notes"
            className="h-9 pl-8"
            data-testid={`${prefix}-search-input`}
          />
        </div>
        <Select
          value={filters.status}
          onValueChange={(v) => setFilters({ ...filters, status: v })}
        >
          <SelectTrigger className="h-9 w-[150px]" data-testid={`${prefix}-filter-status`}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {opts.statuses?.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.sort}
          onValueChange={(v) => setFilters({ ...filters, sort: v })}
        >
          <SelectTrigger className="h-9 w-[170px]" data-testid={`${prefix}-filter-sort`}>
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Sort: default</SelectItem>
            <SelectItem value="name-asc">Name: A → Z</SelectItem>
            <SelectItem value="name-desc">Name: Z → A</SelectItem>
            <SelectItem value="balance-desc">Balance: high → low</SelectItem>
            <SelectItem value="balance-asc">Balance: low → high</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <span className="text-[12px] text-zinc-500 ml-1">Bal ≥</span>
          <Input
            type="number"
            value={filters.minBalance}
            onChange={(e) => setFilters({ ...filters, minBalance: e.target.value })}
            className="h-9 w-[100px]"
            placeholder="500"
            data-testid={`${prefix}-filter-min-balance`}
          />
        </div>
        <button
          onClick={() => setFilters({ ...filters, staleOnly: !filters.staleOnly })}
          className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-[12.5px] transition-colors ${
            filters.staleOnly
              ? "bg-amber-50 border-amber-300 text-amber-800"
              : "hairline bg-white text-zinc-700 hover:bg-zinc-50"
          }`}
          data-testid={`${prefix}-filter-stale`}
        >
          Not updated 7d+
          {staleCount > 0 && (
            <span className="font-mono text-[11px] px-1.5 py-px rounded bg-amber-200 text-amber-900">
              {staleCount}
            </span>
          )}
        </button>
        {activeFilters > 0 && (
          <Button
            variant="ghost"
            className="h-9 text-zinc-600"
            onClick={() =>
              setFilters({
                q: "",
                status: "all",
                sort: "default",
                minBalance: "",
                staleOnly: false,
              })
            }
          >
            <X size={13} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Name</th>
                {enableCard && (
                  <th className="py-3 px-4 font-medium">Card · last 4</th>
                )}
                {enableCashappLinks && (
                  <th className="py-3 px-4 font-medium">Linked CashApps</th>
                )}
                {processorMode && (
                  <th className="py-3 px-4 font-medium text-right">Charge %</th>
                )}
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium text-right">
                  {processorMode ? "Balance (USDT)" : "Balance"}
                </th>
                {processorMode && (
                  <th className="py-3 px-4 font-medium text-right">≈ USD (incl. charge)</th>
                )}
	                <th className="py-3 px-4 font-medium text-right">Actions</th>
	              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={colCount} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : sortedList.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="py-10 text-center text-zinc-500">
                    {activeFilters > 0
                      ? "No entries match these filters."
                      : `No ${title.toLowerCase()} yet.`}
                  </td>
                </tr>
              ) : (
                sortedList.map((w) => (
                  <tr
                    key={w.id}
                    className={`row-hover ${
                      isStale(w.updated_at) ? "bg-amber-50/40" : ""
                    }`}
                    data-testid={`${prefix}-row-${w.id}`}
                  >
                    <td className="py-3 px-4 font-medium">{w.name}</td>
                    {enableCard && (
                      <td className="py-3 px-4 font-mono text-[12.5px]">
                        {w.card_number ? (
                          <span className="text-zinc-700">
                            •••• {String(w.card_number).slice(-4)}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                    )}
                    {enableCashappLinks && (
                      <td className="py-3 px-4">
                        {w.linked_cashapps && w.linked_cashapps.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {w.linked_cashapps.map((c) => (
                              <span
                                key={c.id}
                                className="inline-flex items-center gap-1 text-[11.5px] text-zinc-700 border hairline rounded px-1.5 py-0.5"
                              >
                                <Link2 size={11} strokeWidth={1.7} />
                                {c.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-zinc-400 text-[12px]">—</span>
                        )}
                      </td>
                    )}
                    {processorMode && (
                      <td className="py-3 px-4 font-mono text-right">
                        {Number(w.charge_percent || 0).toFixed(2)}%
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex text-[11.5px] px-1.5 py-0.5 rounded border capitalize ${
                          w.status === "active"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-zinc-100 border-zinc-200 text-zinc-600"
                        }`}
                      >
                        {w.status || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <button
                        type="button"
                        onClick={() => openUpdateBalance(w)}
                        className="font-mono text-right text-zinc-800 underline-offset-2 hover:text-sky-800 hover:underline"
                        title="Update balance"
                        data-testid={`quick-update-balance-${w.id}`}
                      >
                        {processorMode
                          ? Number(w.balance || 0).toLocaleString("en-US", {
                              maximumFractionDigits: 4,
                            })
                          : fmtUSD(w.balance)}
                      </button>
                    </td>
                    {processorMode && (
                      <td className="py-3 px-4 font-mono text-right text-emerald-700">
                        {fmtUSD(usdtToUsd(w.balance, w.charge_percent))}
                      </td>
                    )}
	                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <WalletActions
                          wallet={w}
                          onMutate={load}
                          processor={processorMode}
                          actions={
                            processorMode ? ["balance", "withdraw", "history"] : undefined
                          }
                        />
                        <button
                          onClick={() => openEdit(w)}
                          className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                          data-testid={`edit-${prefix}-${w.id}`}
                        >
                          <Pencil size={14} strokeWidth={1.6} />
                        </button>
                        <button
                          onClick={() => remove(w)}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                          data-testid={`delete-${prefix}-${w.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid={`${prefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Edit ${title}` : `Add ${title.replace(/s$/, "")}`}
            </DialogTitle>
            <DialogDescription>
              Account information and linkage.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid={`${prefix}-name-input`}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger data-testid={`${prefix}-status-select`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opts.statuses?.map((g) => (
                    <SelectItem key={g} value={g} className="capitalize">
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {enableCard && (
              <div className="space-y-1.5">
                <Label>Card · last 4</Label>
                <Input
                  value={form.card_number || ""}
                  onChange={(e) =>
                    setForm({ ...form, card_number: e.target.value })
                  }
                  placeholder="e.g. 4242"
                  maxLength={4}
                  inputMode="numeric"
                  data-testid={`${prefix}-card-input`}
                />
              </div>
            )}
            {enableCashappLinks && (
              <div className="col-span-2 space-y-1.5">
                <Label>Linked CashApps</Label>
                {form.linked_cashapps && form.linked_cashapps.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {form.linked_cashapps.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-2 py-0.5 bg-zinc-50"
                      >
                        <Link2 size={11} strokeWidth={1.7} /> {c.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-[12px] text-zinc-500">
                    None yet — open the CashApp page and link this {title.toLowerCase().replace(/s$/, "")} from there.
                  </div>
                )}
              </div>
            )}
            {processorMode && (
              <div className="col-span-2 space-y-1.5">
                <Label>Charge % (Original USD = USDT ÷ (1 − fee%))</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.charge_percent ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, charge_percent: e.target.value })
                  }
                  placeholder="e.g. 5"
                  data-testid={`${prefix}-charge-input`}
                />
              </div>
            )}
            <div className="space-y-1.5 col-span-2">
              <Label>
                {processorMode ? "Opening balance (USDT)" : "Opening balance (USD)"}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.balance}
                onChange={(e) => setForm({ ...form, balance: e.target.value })}
                disabled={!!editing}
                data-testid={`${prefix}-balance-input`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid={`save-${prefix}-btn`}
            >
              {editing ? "Save changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update balance */}
      <Dialog open={!!updateBalanceFor} onOpenChange={(v) => !v && setUpdateBalanceFor(null)}>
        <DialogContent data-testid="update-balance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Update balance</DialogTitle>
            <DialogDescription>
              Set the current {processorMode ? "USDT" : "USD"} balance for{" "}
              {updateBalanceFor?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Current balance</Label>
              <Input
                value={
                  processorMode
                    ? Number(updateBalanceFor?.balance || 0).toLocaleString("en-US", {
                        maximumFractionDigits: 4,
                      })
                    : fmtUSD(updateBalanceFor?.balance || 0)
                }
                disabled
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label>New balance ({processorMode ? "USDT" : "USD"}) *</Label>
              <Input
                type="number"
                step={processorMode ? "0.0001" : "0.01"}
                value={updateBalanceValue}
                onChange={(e) => setUpdateBalanceValue(e.target.value)}
                data-testid="update-balance-value-input"
              />
            </div>
            {processorMode && (
              <div className="text-[11px] text-zinc-500 font-mono">
                Change value ≈{" "}
                {fmtUSD(
                  usdtToUsd(
                    (Number(updateBalanceValue) || 0) -
                      Number(updateBalanceFor?.balance || 0),
                    updateBalanceFor?.charge_percent
                  )
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={updateBalanceNotes}
                onChange={(e) => setUpdateBalanceNotes(e.target.value)}
                placeholder="Optional"
                data-testid="update-balance-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateBalanceFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitUpdateBalance}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-update-balance-btn"
            >
              Save balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage statuses */}
      <Dialog open={optsOpen} onOpenChange={setOptsOpen}>
        <DialogContent data-testid={`${prefix}-options-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">Manage {title} statuses</DialogTitle>
            <DialogDescription>
              These statuses appear in the form and the filter bar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-1.5">
            <Input
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addStatus())}
              placeholder="e.g. shadow-banned"
              className="h-9"
              data-testid={`${prefix}-status-input`}
            />
            <Button
              onClick={addStatus}
              className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              <Plus size={13} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {optsDraft.map((s, i) => (
              <span
                key={`${s}-${i}`}
                className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-1.5 py-0.5 bg-zinc-50"
              >
                {s}
                <button
                  onClick={() => setOptsDraft(optsDraft.filter((_, idx) => idx !== i))}
                  className="text-zinc-400 hover:text-rose-600"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOptsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveOpts}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid={`save-${prefix}-options-btn`}
            >
              Save statuses
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
